<script setup>
import { computed, onMounted, ref, watch } from "vue";

import { usersInviteSent } from "@/analytics/events";
import { trackEvent } from "@/analytics/track";
import { TeamHandler } from "@/handlers/teamHandler";
import { useToastStore } from "@/stores/toast";
import { validateEmail } from "@/utils/validation";

import BaseButton from "./BaseButton.vue";
import ComboSelect from "./ComboSelect.vue";

const props = defineProps({
  // where the invite composer lives — "users_page" | "quickstart"
  source: { type: String, required: true },
  initialEmails: { type: String, default: "" },
});
const emit = defineEmits(["invited"]);

const teamHandler = new TeamHandler();
const toastStore = useToastStore();
const teams = ref([]);
const teamsLoading = ref(false);
const teamId = ref("");
const emailInput = ref(null);
const emailText = ref(props.initialEmails);
const emailInputInvalid = ref(false);
const error = ref("");
const info = ref("");
const sending = ref(false);

const teamOptions = computed(() =>
  teams.value.map((team) => ({
    label: team.art?.name ? `${team.name} · ${team.art.name}` : team.name,
    value: team.id,
  })),
);
const canSend = computed(() => emailText.value.trim().length > 0 && teamId.value !== "");

onMounted(async () => {
  teamsLoading.value = true;
  try {
    const res = await teamHandler.getTeams();
    if (!res.success) {
      error.value = "Could not load teams.";
      return;
    }
    teams.value = res.data || [];
  } finally {
    teamsLoading.value = false;
  }
});

function clearMessages() {
  error.value = "";
  info.value = "";
}

// Switching team starts a new attempt — stale results would mislead
watch(teamId, clearMessages);

function parseEmails(value) {
  return [
    ...new Set(
      value
        .split(/\r?\n/)
        .map((email) => email.trim())
        .filter(Boolean),
    ),
  ];
}

function onEmailInput(value) {
  emailText.value = value;
  emailInputInvalid.value = false;
  clearMessages();
}

async function sendInvitations() {
  const emails = parseEmails(emailText.value);
  const invalidEmails = emails.filter((email) => !validateEmail(email));
  if (!emails.length || invalidEmails.length > 0) {
    emailInputInvalid.value = true;
    error.value =
      invalidEmails.length > 0
        ? `Not valid email address${invalidEmails.length === 1 ? "" : "es"}: ${invalidEmails.join(", ")}`
        : "Add at least one email address.";
    return;
  }

  sending.value = true;
  clearMessages();

  try {
    // `updateTeam` reconciles the team to the exact member list it's given —
    // there's no additive invite endpoint, so load current members and merge
    // the new invitees onto them (same pattern as OnboardSuccessDialog).
    const [teamRes, membersRes] = await Promise.all([
      teamHandler.getTeam(teamId.value),
      teamHandler.getTeamMembers(teamId.value),
    ]);
    // Guard members too: getTeamMembers goes through catched() and never throws —
    // on failure it resolves with data undefined. updateTeam reconciles to the exact
    // list given, so proceeding with an empty members[] would wipe the team's roster.
    if (!teamRes.success || !teamRes.data || !membersRes.success) {
      throw new Error("Could not load team details.");
    }
    const team = teamRes.data;
    if (team.oidc_group) {
      throw new Error(
        `Members of ${team.name} are managed via OpenID Connect group "${team.oidc_group}". Add users in your identity provider.`,
      );
    }
    const members = membersRes.data || [];

    const seen = new Set(members.map((m) => m.email?.toLowerCase()).filter(Boolean));
    const newMembers = [];
    for (const email of emails) {
      const key = email.toLowerCase();
      if (seen.has(key)) continue;
      seen.add(key);
      newMembers.push({ email });
    }

    if (newMembers.length === 0) {
      // Nothing was sent — keep the typed emails so the user can correct
      info.value = `Everyone you entered is already on ${team.name}.`;
      return;
    }

    await teamHandler.updateTeam(
      teamId.value,
      team.name,
      null,
      [...members, ...newMembers],
      team.art_id,
    );
    trackEvent(usersInviteSent(newMembers.length, props.source));
    const count = newMembers.length;
    toastStore.showMessage({
      message:
        count === 1 ? `Invitation sent to ${newMembers[0].email}.` : `${count} invitations sent.`,
    });
    emailText.value = "";
    emailInput.value?.focus();
    emit("invited", count);
  } catch (e) {
    error.value = e.userMessage || e.message || "Could not send invitations.";
  } finally {
    sending.value = false;
  }
}
</script>

<template>
  <div class="invite-users-form">
    <ComboSelect
      v-model="teamId"
      label="Team they'll join"
      :options="teamOptions"
      :loading-options="teamsLoading"
      :list-max-height="150"
      placeholder="Pick a team..."
      search-placeholder="Search teams..."
      info="Invited users join as team members. You can change their role later."
    />

    <div class="field">
      <label class="field-label" :for="`invite-emails-${source}`">Email addresses</label>
      <textarea
        :id="`invite-emails-${source}`"
        ref="emailInput"
        class="email-input"
        :class="{ error: emailInputInvalid }"
        :value="emailText"
        placeholder="jane@company.com&#10;bob@company.com"
        :aria-invalid="emailInputInvalid ? 'true' : 'false'"
        @input="onEmailInput($event.target.value)"
      />
      <p class="field-hint">Add one email address per line.</p>
    </div>

    <div class="actions">
      <p v-if="error" class="message error" role="alert">{{ error }}</p>
      <p v-else-if="info" class="message info" role="status">{{ info }}</p>
      <BaseButton
        variant="filled"
        color="primary"
        :disabled="!canSend"
        :loading="sending"
        @click="sendInvitations"
      >
        Send invites
      </BaseButton>
    </div>
  </div>
</template>

<style lang="scss" scoped>
@use "@/colors" as c;
@use "@/styles/mixins/typography";

.invite-users-form {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.field {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.field-label {
  @include typography.small("semi-bold");

  color: c.$text-color;
}

.field-hint {
  @include typography.extra-small;

  color: c.$text-subtle;
  margin: 0;
}

.email-input {
  @include typography.small;

  box-sizing: border-box;
  width: 100%;
  min-height: 140px;
  padding: 9px 12px;
  resize: vertical;
  border: 1px solid c.$border-subtle;
  border-radius: 6px;
  outline: none;
  color: c.$black;
  background-color: c.$white;

  &::placeholder {
    color: c.$text-subtle;
  }

  &:focus {
    border-color: c.$main;
  }

  &.error {
    border-color: c.$text-error-color;
  }
}

.actions {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 12px;
}

.message {
  @include typography.small;

  margin: 0;
  flex: 1;

  &.error {
    color: c.$text-error-color;
  }

  &.info {
    color: c.$text-subtle;
  }
}
</style>
